import styled, { css } from "styled-components";

interface TrackSCProps {
    top: string;
    height: string;
    shadow: string;
    layer: string | number;
    right: string;
}
export const HorizontalLineSC = styled.div<{ bottom?: string; isactive?: string }>`
    opacity: 0;
    transition: opacity 0.2s;
    position: absolute;
    width: 200vw;
    height: 0;
    left: -100vw;
    border: 1px solid #ff7606;
    ${({ bottom }) =>
        bottom === "true" &&
        css`
            bottom: 0;
        `};
`;

export const AudioEditWrapperSC = styled.div`
    position: absolute;
    color: #ff7606;
    top: 0;
    left: 0;
    display: none;
    z-index: 3;
`;

export const TrackSC = styled.div<TrackSCProps>`
    top: ${({ top }) => top};
    height: ${({ height }) => height};
    box-shadow: ${({ shadow }) => shadow};
    z-index: ${({ layer }) => layer};
    right: ${({ right }) => (right ? "var(--audio-time-width)" : 0)};
    opacity: 0.81;
    color: #6e6e6e;
    background-color: #000dff;
    border-radius: 0.5em;
    transition:
        color 0.2s,
        background-color 0.2s,
        box-shadow 0.2s,
        opacity 0.2s;
    width: var(--audio-track-width);
    position: absolute;
    cursor: pointer;

    &:hover {
        ${HorizontalLineSC} {
            opacity: 1;
        }
        ${AudioEditWrapperSC} {
            display: block;
        }
    }
`;

export const TrackEmptySC = styled.div<TrackSCProps>`
    top: ${({ top }) => top};
    height: ${({ height }) => height};
    box-shadow: ${({ shadow }) => shadow};
    z-index: ${({ layer }) => layer};
    right: ${({ right }) => (right ? "var(--audio-time-width)" : 0)};
    opacity: 0.81;
    color: #6e6e6e;
    width: var(--audio-track-width);
    position: absolute;
    cursor: pointer;
    border-radius: 0.5em;
    box-shadow:
        0 1px #ffffff96,
        inset 0 3px 8px #0000,
        inset 0 3px 11px -6px #000000b8;
`;

/**
 * Озвучка без реплики оригинала: в видео её быть не должно, и в редакторе она молчит. Красная
 * пунктирная рамка вместо волны — чтобы её не приняли ни за обычный дубль, ни за пустое место
 * (4119e839, ru_es, папка 1, 1:22, «Así es.», 02.10.2026).
 */
export const TrackOrphanSC = styled.div<Omit<TrackSCProps, "shadow">>`
    top: ${({ top }) => top};
    height: ${({ height }) => height};
    z-index: ${({ layer }) => layer};
    right: ${({ right }) => (right ? "var(--audio-time-width)" : 0)};
    width: var(--audio-track-width);
    position: absolute;
    box-sizing: border-box;
    border: 2px dashed #ff4d4f;
    border-radius: 0.5em;
    background-color: rgba(255, 77, 79, 0.12);
    color: #cf1322;
    cursor: help;
    /* Не overflow: hidden — метка времени (CueTime) стоит справа, за краем блока. */
`;

export const OrphanTextSC = styled.div`
    padding: 0 1.6em 0 0.4em;
    font-size: 11px;
    line-height: 14px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: none;
`;

export const OrphanDeleteSC = styled.button`
    position: absolute;
    top: 0;
    right: 0;
    z-index: 3;
    padding: 0 0.25em;
    border: none;
    background: transparent;
    font-size: 12px;
    line-height: 14px;
    cursor: pointer;

    &:hover {
        background: rgba(255, 77, 79, 0.25);
    }
`;

export const SelectWrapperSC = styled.div<{ side?: string }>`
    position: absolute;
    bottom: 0;
    left: ${({ side }) => (side === "left" ? "-30%" : "130%")};
    z-index: 3;
    & .ant-checkbox-inner {
        border-color: #1677ff;
    }
`;

/**
 * Полоса блока пересборки у левого края реплики.
 *
 * Блок всё равно перезаливается целиком, поэтому переводчику выгодно сделать в нём все правки
 * разом — отметка показывает границы, а не запрещает править. Жёлтая полоса — папка сдана и
 * пересобирается: правка в этих строках приедет только следующим кругом.
 */
export const SectionMarkSC = styled.div<{ state: "in" | "out" | "rebuilding" }>`
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    z-index: 3;
    pointer-events: none;
    background: ${({ state }) =>
        state === "in" ? "#1677ff" : state === "rebuilding" ? "#faad14" : "transparent"};
    opacity: ${({ state }) => (state === "out" ? 0 : 0.85)};
`;

/**
 * Readiness of this row's voicing, as a stripe on the RIGHT edge.
 *
 * Mirrors SectionMarkSC above, which occupies the left edge for the rebuild block — the two must
 * never collide, because a translator needs to read both at once: "this cue is in the rebuild
 * block" and "this cue can be played right now" are different questions.
 */
export const ClipMarkSC = styled.div<{ state: "idle" | "loading" | "ready" | "error" | "editing" }>`
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    z-index: 3;
    pointer-events: auto;
    cursor: help;
    background: ${({ state }) =>
        state === "ready"
            ? "#52c41a"
            : state === "loading"
              ? "#faad14"
              : state === "error"
                ? "#ff4d4f"
                : state === "editing"
                  ? "#722ed1"
                  : "#d9d9d9"};
    opacity: ${({ state }) => (state === "idle" ? 0.35 : 0.85)};
    animation: ${({ state }) => (state === "loading" ? "clip-mark-pulse 1s ease-in-out infinite" : "none")};

    @keyframes clip-mark-pulse {
        0%,
        100% {
            opacity: 0.35;
        }
        50% {
            opacity: 0.95;
        }
    }
`;
